import { create } from "zustand";
import { persist } from "zustand/middleware";

interface CoachMarkerData {
  id: string;
  title: string;
  description: string;
  placement?: "left" | "top" | "bottom" | "right";
  step?: number;
  totalSteps?: number;
  nextLabel?: string;
  previousLabel?: string;
  element: HTMLElement;
  onNext?: () => void;
  onPrevious?: () => void;
}

interface CoachMarkersState {
  isEnabled: boolean;
  completedMarkers: string[];
  openMarkerId: string | null;
  registeredMarkers: Map<string, CoachMarkerData>;
  markerQueue: string[];
  setEnabled: (enabled: boolean) => void;
  markAsCompleted: (id: string) => void;
  shouldShowMarker: (id: string) => boolean;
  registerMarker: (data: CoachMarkerData) => void;
  unregisterMarker: (id: string) => void;
  showNextMarker: () => void;
  showPreviousMarker: () => void;
  openMarker: (id: string) => void;
  closeMarker: () => void;
  isMarkerOpen: (id: string) => boolean;
  processMarkerQueue: () => void;
  skipAllMarkers: () => void;
}

export const useCoachMarkers = create<CoachMarkersState>()(
  persist(
    (set, get) => ({
      isEnabled: true, // Changed from false to true - will be overridden by localStorage if it exists
      completedMarkers: [],
      openMarkerId: null,
      registeredMarkers: new Map(),
      markerQueue: [],

      setEnabled: (enabled: boolean) => {
        set({ isEnabled: enabled });
        if (enabled) {
          // Process any pending markers immediately
          setTimeout(() => {
            get().processMarkerQueue();
          }, 100);
        } else {
          // Close any open marker when disabled
          set({ openMarkerId: null });
        }
      },

      markAsCompleted: (id) =>
        set((state) => ({
          completedMarkers: [...state.completedMarkers, id],
        })),

      shouldShowMarker: (id) => {
        const state = get();

        return state.isEnabled && !state.completedMarkers.includes(id);
      },

      registerMarker: (data) => {
        const state = get();
        const newRegisteredMarkers = new Map(state.registeredMarkers);

        newRegisteredMarkers.set(data.id, data);

        set({
          registeredMarkers: newRegisteredMarkers,
          markerQueue: [...state.markerQueue.filter((id) => id !== data.id), data.id],
        });

        // Process queue if enabled
        if (state.isEnabled) {
          setTimeout(() => {
            get().processMarkerQueue();
          }, 100);
        }
      },

      unregisterMarker: (id) => {
        const state = get();
        const newRegisteredMarkers = new Map(state.registeredMarkers);

        newRegisteredMarkers.delete(id);

        set({
          registeredMarkers: newRegisteredMarkers,
          markerQueue: state.markerQueue.filter((queueId) => queueId !== id),
          openMarkerId: state.openMarkerId === id ? null : state.openMarkerId,
        });
      },

      processMarkerQueue: () => {
        const state = get();

        if (!state.isEnabled || state.openMarkerId) return;

        // Find the first marker in queue that should be shown
        const nextMarkerId = state.markerQueue.find(
          (id) => state.shouldShowMarker(id) && state.registeredMarkers.has(id),
        );

        if (nextMarkerId) {
          set({ openMarkerId: nextMarkerId });
        }
      },

      showNextMarker: () => {
        const state = get();
        const activeMarker = state.openMarkerId
          ? state.registeredMarkers.get(state.openMarkerId)
          : null;

        if (activeMarker?.onNext) {
          activeMarker.onNext();
        }

        // Mark current as completed and close
        if (state.openMarkerId) {
          get().markAsCompleted(state.openMarkerId);
        }

        set({ openMarkerId: null });

        // Process next marker
        setTimeout(() => {
          get().processMarkerQueue();
        }, 100);
      },

      showPreviousMarker: () => {
        const state = get();
        const activeMarker = state.openMarkerId
          ? state.registeredMarkers.get(state.openMarkerId)
          : null;

        if (activeMarker?.onPrevious) {
          activeMarker.onPrevious();
        }

        set({ openMarkerId: null });
      },

      openMarker: (id) => {
        const state = get();

        if (state.shouldShowMarker(id) && state.registeredMarkers.has(id)) {
          set({ openMarkerId: id });
        }
      },

      closeMarker: () => {
        set({ openMarkerId: null });
      },

      isMarkerOpen: (id) => {
        const state = get();

        return state.openMarkerId === id;
      },

      skipAllMarkers: () => {
        const state = get();
        const remainingMarkers = state.markerQueue.filter(
          (id) => !state.completedMarkers.includes(id),
        );

        remainingMarkers.forEach((id) => {
          get().markAsCompleted(id);
        });

        set({ openMarkerId: null });
      },
    }),
    {
      name: "coach-markers-storage",
      partialize: (state) => ({
        isEnabled: state.isEnabled,
        completedMarkers: state.completedMarkers,
      }),
      onRehydrateStorage: () => (state) => {
        // If state is null, it means localStorage doesn't exist yet (first-time user)
        // In this case, we want to enable coach marks by default
        if (state === null) {
          // The default state will be used (isEnabled: true)
          return;
        }
        // If state exists, use the stored values
      },
    },
  ),
);
